import React from 'react';

import { useMutation } from '@lumapps/base-api/react-query';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL } from '@lumapps/translations';

import { getDeveloperPortalLoginUrl } from '../api/loginUrl';
import { getDeveloperPortalUrl } from '../utils/getDeveloperPortalUrl';

export const useOpenDeveloperPortal = () => {
    const { error: notifyError } = useNotification();
    // Synchronous in-flight flag: `mutation.isLoading` only updates on the next render,
    // so two clicks fired in the same tick (before React re-renders) would both pass a
    // state-based guard. A ref is read/written synchronously, so the second call bails
    // out immediately instead of opening a second (blank) tab.
    const isOpeningRef = React.useRef(false);

    const mutation = useMutation({
        mutationFn: () => getDeveloperPortalLoginUrl(),
    });

    const openDeveloperPortal = () => {
        if (isOpeningRef.current) {
            return;
        }
        isOpeningRef.current = true;

        // Open the tab synchronously (popup-safe) before the async fetch.
        const tab = window.open('', '_blank');

        const openUrl = (url: string) => {
            if (tab) {
                tab.location.href = url;
            } else {
                window.location.assign(url);
            }
        };

        mutation.mutate(undefined, {
            onSuccess: ({ url }) => {
                openUrl(url);
            },
            onError: () => {
                // Graceful fallback: send the already-open tab to the unauthenticated
                // portal so the user still reaches it, then surface the error.
                openUrl(getDeveloperPortalUrl());
                notifyError({ translate: GLOBAL.GENERIC_ERROR });
            },
            // Clear the in-flight guard once the request settles (success OR error),
            // so a hung/failed mint can't leave the entry stuck disabled.
            onSettled: () => {
                isOpeningRef.current = false;
            },
        });
    };

    return { openDeveloperPortal, isLoading: mutation.isLoading };
};
